/**
 * "This variable is meant to appear on several forms."
 *
 * A confirmation is ONE FIELD in ONE DOCUMENT of its own
 * (projects/{p}/crfBuilder/crossFormConfirmations), keyed on the variable name
 * trimmed and upper-cased, written with a merge. Until 2026-09-12 it was an
 * entry in an array inside the study's whole Builder document, so any page
 * saving a stale copy of that document erased every confirmation in the study:
 * watched live on clinical-dev-9382, 43 confirmations deleted by the next page
 * load, twice within a minute.
 *
 * Confirmations recorded in the old place are still honoured, so nothing
 * anyone has already confirmed is lost.
 *
 * SCOPE (Jim, 2026-09-12): a confirmation applies study-wide, as it always
 * has. `forms` records which forms the variable appeared on when it was
 * confirmed; nothing reads it yet. Whether a NEW form carrying a confirmed
 * variable should raise the warning again is a separate decision.
 *
 * Pure so it can be tested under functions' vitest (`npm run test:web`).
 */

/** The document that holds one field per confirmed variable. */
export const CROSS_FORM_CONFIRMATIONS_DOC = 'crossFormConfirmations';

export interface CrossFormConfirmationEntry {
  /** The forms the variable appeared on when it was confirmed. Metadata. */
  forms: string[];
  /** When it was confirmed. */
  at?: unknown;
}

/** The key a confirmation is stored and matched under. */
export function normaliseConfirmationKey(name: string | undefined | null): string {
  return String(name ?? '').trim().toUpperCase();
}

/**
 * Every confirmed key, from the confirmations document and from the old
 * session list, normalised so the two agree with the server's own comparison.
 */
export function confirmedKeysFrom(sources: {
  stored?: unknown;
  legacy?: unknown;
}): Set<string> {
  const keys = new Set<string>();
  const add = (raw: unknown) => {
    const k = normaliseConfirmationKey(typeof raw === 'string' ? raw : '');
    if (k) keys.add(k);
  };
  if (sources.stored && typeof sources.stored === 'object') {
    for (const k of Object.keys(sources.stored as Record<string, unknown>)) add(k);
  }
  if (Array.isArray(sources.legacy)) {
    for (const v of sources.legacy) add(v);
  }
  return keys;
}

/** Whether a cross-form duplicate warning has been confirmed. */
export function isCrossFormDuplicateConfirmed(
  field: string | undefined | null,
  confirmedKeys: Set<string>,
): boolean {
  const k = normaliseConfirmationKey(field);
  return !!k && confirmedKeys.has(k);
}

/** What one confirmation stores: the forms it covered, and when. */
export function buildConfirmationEntry(formIds: string[], at: Date): CrossFormConfirmationEntry {
  return { forms: [...new Set(formIds.filter(Boolean))].sort(), at };
}
